import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/shared/api/axios';
import { useAuthStore } from '@/entities/session/model/authStore';
import { isValidOtp, normalizeOtpInput } from '@/shared/lib/validators';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, shadow, radius } from '@/shared/theme/tokens';
import { ROUTES } from '@/shared/config/constants';

const RESEND_SECONDS = 60;

export default function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const c = useThemeColors();
  const email = location.state?.email ?? '';
  const { setAuth } = useAuthStore();

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!email) navigate(ROUTES.LOGIN, { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  function handleOtpChange(raw) {
    const normalized = normalizeOtpInput(raw);
    setOtp(normalized);
    setError('');
  }

  function handlePaste(e) {
    e.preventDefault();
    setOtp(normalizeOtpInput(e.clipboardData.getData('text')));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!isValidOtp(otp)) {
      setError('Enter the 6-digit code sent to your email.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/otp/verify', { email, otp });
      setAuth({ token: data.token, user: data.user, assignments: data.assignments });
      navigate(ROUTES.DEPLOYMENTS, { replace: true });
    } catch (err) {
      setError(err.message ?? 'Invalid or expired code. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setError('');
    try {
      await api.post('/auth/otp/request', { email });
      setResendTimer(RESEND_SECONDS);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setResendTimer((t) => {
          if (t <= 1) { clearInterval(timerRef.current); return 0; }
          return t - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message ?? 'Failed to resend code.');
    } finally {
      setResending(false);
    }
  }

  const otpFilled = otp.length === 6;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: c.bgPage,
      position: 'relative',
      overflow: 'hidden',
      padding: '24px 16px',
      fontFamily: typography.fontFamily.body,
    }}>
      {/* Background layers */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: [
          'linear-gradient(to right, rgba(196,198,211,0.4) 1px, transparent 1px)',
          'linear-gradient(to bottom, rgba(196,198,211,0.4) 1px, transparent 1px)',
        ].join(', '),
        backgroundSize: '32px 32px',
      }} />
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'radial-gradient(ellipse 120% 80% at 50% -10%, rgba(32,36,117,0.06) 0%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px',
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(62,181,231,0.08) 0%, transparent 70%)',
        zIndex: 1, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-100px', left: '-100px', width: '500px', height: '500px',
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(32,36,117,0.07) 0%, transparent 70%)',
        zIndex: 1, pointerEvents: 'none',
      }} />

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 10, width: '100%', maxWidth: '420px',
        background: c.bg, borderRadius: radius['3xl'], border: `1px solid ${c.borderSubtle}`,
        boxShadow: shadow.cardFull,
        overflow: 'hidden',
      }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, #202475 0%, #1e4396 40%, #3EB5E7 100%)' }} />

        <div style={{ padding: '36px 40px 28px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: radius.xl,
              background: 'linear-gradient(135deg, #202475 0%, #1e4396 60%, #3EB5E7 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: shadow.btnSm,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 26, color: '#ffffff', fontVariationSettings: "'FILL' 1, 'wght' 400" }}>
                widgets
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.normal, lineHeight: 1.2 }}>
                Industrial AutoPilot
              </span>
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: c.textMuted, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase' }}>
                by Ripik AI
              </span>
            </div>
          </div>

          <div style={{ height: '1px', background: `linear-gradient(90deg, ${c.borderSubtle} 0%, transparent 100%)`, marginBottom: '20px' }} />

          {/* Email context chip */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', marginBottom: '16px',
            background: 'linear-gradient(135deg, rgba(62,181,231,0.08) 0%, rgba(32,36,117,0.05) 100%)',
            border: `1px solid ${semantic.brandBorderAccentMid}`, borderRadius: radius.full, padding: '5px 12px',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, color: semantic.brandAccent, marginRight: 6 }}>mail</span>
            <span style={{ fontSize: typography.fontSize.base, color: c.textActive, fontWeight: typography.fontWeight.semibold }}>{email}</span>
          </div>

          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.tight, lineHeight: 1.3, margin: '0 0 8px' }}>
              Check your inbox
            </h1>
            <p style={{ fontSize: typography.fontSize.lg, color: c.textMuted, lineHeight: 1.6, margin: 0, fontWeight: typography.fontWeight.normal }}>
              We sent a 6-digit verification code. It expires in 10 minutes.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textLabel, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase' }} htmlFor="otp">
                Verification Code
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={otp}
                onChange={(e) => handleOtpChange(e.target.value)}
                onPaste={handlePaste}
                placeholder="000000"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: c.bgSubtle,
                  border: `1.5px solid ${error ? semantic.error : otpFilled ? semantic.brandAccent : c.borderInput}`,
                  borderRadius: radius.lg,
                  padding: '14px 20px',
                  fontSize: typography.fontSize['7xl'],
                  fontFamily: typography.fontFamily.body,
                  fontWeight: typography.fontWeight.semibold,
                  letterSpacing: typography.letterSpacing.otp,
                  color: c.textPrimary,
                  textAlign: 'center',
                  outline: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxShadow: otpFilled && !error
                    ? '0 0 0 3px rgba(62,181,231,0.12)'
                    : error
                    ? '0 0 0 3px rgba(186,26,26,0.10)'
                    : 'none',
                }}
              />
              {/* Progress dots */}
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '7px', height: '7px', borderRadius: '50%',
                      background: i < otp.length ? semantic.brandAccent : c.borderSubtle,
                      transform: i < otp.length ? 'scale(1.15)' : 'scale(1)',
                      transition: 'background 0.15s, transform 0.15s',
                    }}
                  />
                ))}
              </div>
              {error && (
                <p style={{ fontSize: typography.fontSize.base, color: semantic.error, margin: 0, display: 'flex', alignItems: 'center', fontWeight: typography.fontWeight.medium }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4, verticalAlign: 'middle' }}>error</span>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !otpFilled}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: radius.lg,
                padding: '13px 20px',
                fontSize: typography.fontSize.md,
                fontFamily: typography.fontFamily.body,
                fontWeight: typography.fontWeight.semibold,
                letterSpacing: typography.letterSpacing.wider,
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'opacity 0.15s, box-shadow 0.15s',
                opacity: loading || !otpFilled ? 0.6 : 1,
                cursor: loading || !otpFilled ? 'not-allowed' : 'pointer',
                background: otpFilled ? 'linear-gradient(90deg, #202475 0%, #1e4396 50%, #2a7ab5 100%)' : c.textSubtle,
                boxShadow: otpFilled ? shadow.btnPrimary : 'none',
              }}
            >
              {loading ? (
                <span className="material-symbols-outlined" style={{ fontSize: 18, animation: 'spin 1s linear infinite' }}>refresh</span>
              ) : (
                <>
                  <span>Verify & Sign In</span>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>verified_user</span>
                </>
              )}
            </button>
          </form>

          {/* Resend */}
          <div style={{ textAlign: 'center', marginTop: '16px', fontSize: typography.fontSize.md, color: c.textMuted }}>
            <span style={{ color: c.textSubtle }}>Didn&apos;t receive it? </span>
            {resendTimer > 0 ? (
              <span style={{ color: c.textActive, fontWeight: typography.fontWeight.semibold }}>Resend in {resendTimer}s</span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                style={{
                  background: 'none', border: 'none', color: c.textActive, fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.md, cursor: 'pointer', padding: 0,
                  fontFamily: typography.fontFamily.body,
                  textDecoration: 'underline', textUnderlineOffset: '2px',
                }}
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </div>

          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              background: 'none', border: 'none', color: c.textSubtle, fontSize: typography.fontSize.base,
              cursor: 'pointer', fontFamily: typography.fontFamily.body,
              marginTop: '12px', padding: '4px', transition: 'color 0.15s', width: '100%',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_back</span>
            Use a different email
          </button>
        </div>

        {/* Security badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 40px 14px', borderTop: `1px solid ${c.divider}`, background: c.bgSubtle }}>
          <span className="material-symbols-outlined" style={{ fontSize: 12, color: c.textSubtle, marginRight: 4 }}>lock</span>
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide }}>Secured by Ripik AI</span>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
